import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, ArrowRight, ChevronRight } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";

const STATUS = {
  draft: "bg-white/15 text-white/70",
  in_progress: "bg-blue-500/25 text-blue-300",
  completed: "bg-emerald-500/25 text-emerald-300",
  signed: "bg-purple-500/25 text-purple-300",
};

export default function Inspections() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState({});
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      CRM_API.get("/inspections"),
      CRM_API.get("/customers"),
      CRM_API.get("/vehicles"),
    ]).then(([insp, cust, veh]) => {
      setRows(Array.isArray(insp.data) ? insp.data : []);
      const cl = Array.isArray(cust.data) ? cust.data : cust.data.items || [];
      const vl = Array.isArray(veh.data) ? veh.data : veh.data.items || [];
      setCustomers(Object.fromEntries(cl.map((c) => [c.id, c])));
      setVehicles(Object.fromEntries(vl.map((v) => [v.id, v])));
    }).catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="crm-inspections-page">
      <PageHeader
        eyebrow="Studio"
        title="Inspections"
        subtitle="Check-in reports with photos and damage markers."
        right={(
          <Link to="/crm/check-in" data-testid="btn-new-inspection"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
            <ClipboardCheck className="w-4 h-4" /> New Check-In
          </Link>
        )}
      />
      <div className="px-6 md:px-10 py-6 space-y-2">
        {loading && <div className="p-10 text-white/40 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-12 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm" data-testid="inspections-empty">
            No inspections yet. Start a <Link to="/crm/check-in" className="text-[#8db2ff] hover:text-white underline underline-offset-2">Check-In</Link> when the next vehicle arrives.
          </div>
        )}
        {rows.map((r) => {
          const c = customers[r.customer_id] || {};
          const v = vehicles[r.vehicle_id] || {};
          const photos = (r.photos || []).length;
          const damages = (r.damage_markers || []).length;
          return (
            <div key={r.id} data-testid={`inspection-${r.id}`}
              className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/50 hover:bg-white/[0.07] transition-colors cursor-pointer group"
              onClick={() => nav(`/crm/inspections/${r.id}`)}>
              <ClipboardCheck className="w-4 h-4 text-[#1F5EFF] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-[15px] group-hover:text-[#8db2ff] transition-colors">
                  {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}
                  {v.license_plate ? <span className="text-white/40 text-xs ml-2">{v.license_plate}</span> : null}
                </div>
                <div className="text-white/40 text-xs mt-0.5">
                  {`${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"} · {new Date(r.created_at).toLocaleDateString("en-CA")} · {photos} photos · {damages} markers
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded text-[10px] font-mono-alt uppercase tracking-widest ${STATUS[r.status] || STATUS.draft}`}>
                {r.status || "draft"}
              </span>
              {r.work_order_id && (
                <Link to={`/crm/work-orders/${r.work_order_id}`} data-testid={`inspection-wo-${r.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[#8db2ff] text-xs hover:text-white inline-flex items-center gap-1">
                  Work Order <ArrowRight className="w-3 h-3" />
                </Link>
              )}
              <ChevronRight className="w-4 h-4 text-white/25 group-hover:text-white/60 flex-shrink-0 transition-colors" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
